/** Sticky chat header bar — left: identity, middle: live state, right: quick actions.
 *  Lifted out of AgentFeed to keep that file focused on the feed itself. */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client/react";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";
import { Cloud, Combine, FileDown, MessageSquare, MessageSquarePlus, Sparkles, Wrench } from "lucide-react";

import { useDevAgentStore } from "@/stores/devAgentStore";
import { useShallow } from "zustand/react/shallow";
import { REFRESH_SALESFORCE_TOKEN } from "@/gql/auth";
import { runDevAgentContextCompress } from "../agentFeed/runDevAgentContextCompress";

interface ChatHeaderBarProps {
  /** Connection state from the SSE bridge — drives the live/reconnecting/offline pill. */
  connectionStatus: "connected" | "reconnecting" | "disconnected";
  /** Number of MCP tools available — shown next to the wrench icon. */
  mcpToolCount: number;
  /** Number of user+agent messages in the current session. */
  messageCount: number;
  /** Truthy session title; falls back to "OrgCompass" when empty. */
  currentSessionTitle?: string;
  /** Display name of the currently selected Salesforce org. */
  orgName: string;
  /** True when the agent is running — gates Compress and the disabled state. */
  disabled?: boolean;
  /** Active session (required for compress — same as composer). */
  sessionUuid: string | null;
  /** Windowed token estimate for context — drives Compress enable + title. */
  approxContextTokens: number;
  /** Optional callback for the + New session button. Hidden when not provided. */
  onNewSession?: () => void;
  /** Re-open SSE for the current chat (shown when disconnected). */
  onReconnectSSE?: () => void;
  className?: string;
}

export function ChatHeaderBar({
  connectionStatus,
  mcpToolCount,
  messageCount,
  currentSessionTitle,
  orgName,
  disabled,
  sessionUuid,
  approxContextTokens,
  onNewSession,
  onReconnectSSE,
  className,
}: ChatHeaderBarProps) {
  const { sfConnected, setSfConnected } = useDevAgentStore(
    useShallow((s) => ({ sfConnected: s.sfConnected, setSfConnected: s.setSfConnected }))
  );

  const dotClass =
    connectionStatus === "connected"
      ? "bg-green-500"
      : connectionStatus === "reconnecting"
        ? "bg-amber-500 animate-pulse"
        : "bg-red-500 animate-pulse";
  const labelClass =
    connectionStatus === "connected"
      ? "text-green-500 dark:text-green-400"
      : connectionStatus === "reconnecting"
        ? "text-amber-500 dark:text-amber-400"
        : "text-red-500 dark:text-red-400";
  const label =
    connectionStatus === "connected"
      ? "Live"
      : connectionStatus === "reconnecting"
        ? "Reconnecting…"
        : "Offline";

  /** Force-refresh the Salesforce token. Apollo mutation goes directly to
   *  ncoder-backend (Phoenix) — no agentic-platform involvement. On a
   *  `:no_token` error or "No organizations" the user has never connected
   *  this org; route them to the org-connection page to OAuth from scratch. */
  const navigate = useNavigate();
  const [refreshSf, { loading: refreshingSf }] = useMutation(REFRESH_SALESFORCE_TOKEN);
  const handleSfRefresh = useCallback(async () => {
    if (refreshingSf || sfConnected) return; // green = no-op; use SSE + store as source of truth
    try {
      const { data } = await refreshSf();
      const ok = (data as { refreshSalesforceToken?: { success?: boolean } } | undefined)
        ?.refreshSalesforceToken?.success;
      if (ok) {
        setSfConnected(true);
        toast.success("Salesforce reconnected");
        return;
      }
      toast.error("Could not reconnect — please reauthenticate.");
      navigate("/org/connection");
    } catch (err: unknown) {
      const msg = String(
        (err as { message?: string })?.message ||
          (err as { graphQLErrors?: { message?: string }[] })?.graphQLErrors?.[0]?.message ||
          "Salesforce reconnect failed",
      );
      if (/no_token|no organi[sz]ations/i.test(msg)) {
        navigate("/org/connection");
        return;
      }
      toast.error(msg);
    }
  }, [navigate, refreshSf, refreshingSf, sfConnected, setSfConnected]);

  /** Build a markdown export of the whole conversation client-side and download it. */
  const handleExport = useCallback(() => {
    const items = useDevAgentStore.getState().feedItems;
    const title = currentSessionTitle || "OrgCompass";
    const lines: string[] = [`# ${title}`];
    if (orgName) lines.push(`Org: ${orgName}`);
    lines.push(`Generated: ${new Date().toISOString()}`, "");
    for (const it of items) {
      if (it.type === "chat_user") {
        lines.push("## You", it.data.content || "", "");
      } else if (it.type === "chat_agent") {
        lines.push("## OrgCompass", it.data.content || "", "");
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orgcompass_${title.replace(/[^a-z0-9]+/gi, "_")}_${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Conversation exported as Markdown");
  }, [currentSessionTitle, orgName]);

  return (
    <div
      className={cn(
        "sticky top-0 z-30 isolate bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border",
        className,
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 text-xs overflow-x-auto">
        <div className="flex min-w-0 flex-1 basis-[8rem] items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
          <span
            className="max-w-[min(12rem,40vw)] truncate font-semibold text-foreground"
            title={currentSessionTitle || "OrgCompass"}
          >
            {currentSessionTitle || "OrgCompass"}
          </span>
          {orgName && (
            <>
              <span className="text-muted-foreground/60">•</span>
              <span
                className="flex max-w-[min(10rem,30vw)] items-center gap-1 truncate text-muted-foreground/85"
                title={orgName}
              >
                <Cloud className="h-3 w-3 shrink-0" />
                <span className="truncate">{orgName}</span>
              </span>
            </>
          )}
        </div>

        <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
          <div className="relative z-10 flex shrink-0 flex-wrap items-center gap-2 text-muted-foreground">
            <div className="flex shrink-0 items-center gap-1.5" title={`SSE: ${connectionStatus}`}>
              <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
              <span className={`shrink-0 ${labelClass}`}>{label}</span>
            </div>
            <button
              type="button"
              onClick={handleSfRefresh}
              disabled={refreshingSf || sfConnected}
              title={
                sfConnected
                  ? "Salesforce connected — reconnect not needed"
                  : "Refresh Salesforce token (token issue reported via SSE or after errors)"
              }
              className={cn(
                "pointer-events-auto relative z-10 flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-1 text-[11px] font-medium transition-colors",
                sfConnected &&
                  !refreshingSf &&
                  "[&:disabled]:cursor-default [&:disabled]:opacity-100 border-emerald-600/50 bg-emerald-600/12 text-emerald-800 shadow-sm shadow-emerald-900/10 dark:border-emerald-500/45 dark:bg-emerald-500/15 dark:text-emerald-300 dark:shadow-emerald-950/30",
                refreshingSf && "[&:disabled]:cursor-wait [&:disabled]:opacity-60",
                !sfConnected &&
                  "border-amber-500/35 text-amber-700 dark:text-amber-400 enabled:hover:bg-amber-500/15",
              )}
            >
              <Cloud className="h-3 w-3 shrink-0" />
              <span>
                {refreshingSf ? "Refreshing…" : sfConnected ? "SF connected" : "Reconnect SF"}
              </span>
            </button>
            {mcpToolCount > 0 && (
              <div className="flex shrink-0 items-center gap-1"
                title={`${mcpToolCount} MCP tools available`}
              >
                <Wrench className="h-3 w-3" />
                <span>{mcpToolCount}</span>
              </div>
            )}
            {messageCount > 0 && (
              <div
                className="flex shrink-0 items-center gap-1"
                title={`${messageCount} messages in this session`}
              >
                <MessageSquare className="h-3 w-3" />
                <span>{messageCount}</span>
              </div>
            )}
          </div>

          <div className="relative z-10 flex shrink-0 flex-wrap items-center gap-1">
            {onNewSession && (
            <button
              onClick={() => onNewSession()}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-foreground bg-muted/40 hover:bg-primary/15 hover:text-primary border border-border hover:border-primary/30 transition-colors"
              title="Start a new chat session"
            >
              <MessageSquarePlus className="h-3.5 w-3.5" />
              <span>New chat</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => void runDevAgentContextCompress()}
            disabled={disabled || !sessionUuid || approxContextTokens === 0}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-foreground bg-muted/40 hover:bg-amber-500/15 hover:text-amber-500 dark:hover:text-amber-400 border border-border hover:border-amber-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={`${approxContextTokens.toLocaleString()} tokens in context — same compress as below Send (needs ~30k+ to trim & run)`}
          >
            <Combine className="h-3 w-3" />
            <span>Compress</span>
          </button>
          <button
            onClick={handleExport}
            disabled={messageCount === 0}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-foreground bg-muted/40 hover:bg-emerald-500/15 hover:text-emerald-500 dark:hover:text-emerald-400 border border-border hover:border-emerald-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Export the entire conversation as a Markdown file"
          >
            <FileDown className="h-3 w-3" />
            <span>Export</span>
          </button>
          {connectionStatus === "disconnected" && onReconnectSSE && (
            <button
              type="button"
              onClick={() => {
                onReconnectSSE();
                toast.success("Reconnecting…");
              }}
              className="ml-1 px-2 py-1 rounded-md text-xs font-medium bg-primary/15 text-primary hover:bg-primary/25 border border-primary/30 transition-colors"
            >
              Retry
            </button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}